import { buildCollection, buildProperty, EntityAction } from "@firecms/core";

import {
  hasAnyRole,
  ONBOARDING_QUEUE_ROLES,
  ONBOARDING_VIEW_ROLES,
} from "./apple_delegated_onboarding.roles";
import {
  ONBOARDING_SHOWCASE_LABEL,
  ONBOARDING_SHOWCASE_REQUESTS_PATH,
  OnboardingShowcaseRequestEntity,
  SHOWCASE_CTA_LABELS,
  ShowcaseCreativeCtaEnum,
} from "./apple_delegated_onboarding.types";
import {
  evaluateQueueGate,
  resolveDocumentEnvironment,
  stripFieldsTheFormMayNotWrite,
} from "./apple_delegated_onboarding.utils";
import { fetchOnboardingParent, queueOnboardingStep } from "./apple_delegated_onboarding.write";
import { stepStatusProperties } from "./apple_delegated_onboarding_properties";
import { appleShowcaseLifecycleRequestsCollection } from "./apple_showcase_lifecycle";
import { ShowcaseRequestChip } from "./OnboardingStepChip";
import { OnboardingShowcaseView } from "./OnboardingStepsView";
import { ShowcaseLifecycleView } from "./ShowcaseLifecycleView";

/**
 * Row action mirroring the gated button on the request's "Queue" tab, for the list inside the parent
 * entity where that tab is not on screen.
 *
 * ⚠️ `EntityAction` in FireCMS 3.0.0-beta.14 has no `isEnabled`/`disabled` predicate (documented at
 * `apple_grant_status.ts:28-31`), so the gate runs INSIDE `onClick` and explains itself in a
 * snackbar rather than the action being absent. It calls the SAME `evaluateQueueGate` the panel
 * does, so the two can never give different answers.
 */
const queueShowcaseAction: EntityAction<Partial<OnboardingShowcaseRequestEntity>> = {
  name: "Queue showcase",
  key: "queue_showcase_request",
  onClick: async ({ entity, context }) => {
    const { snackbarController, authController, dataSource } = context;

    if (!hasAnyRole(authController, ONBOARDING_QUEUE_ROLES)) {
      snackbarController.open({
        type: "error",
        message: "You do not have permission to queue Apple onboarding steps.",
      });
      return;
    }

    // The brand and location id pins live on the PARENT document, which FireCMS does not hand a
    // subcollection. A failed read degrades the gate to "pins not checked here" rather than
    // refusing — the backend checks all of them authoritatively before it calls Apple.
    const parent = await fetchOnboardingParent(dataSource, entity.path);
    const gate = evaluateQueueGate({
      target: "showcase",
      step: entity.values,
      parent,
      env: resolveDocumentEnvironment(parent),
    });

    if (!gate.allowed) {
      snackbarController.open({ type: "warning", message: gate.reason });
      return;
    }
    for (const caution of gate.cautions) {
      snackbarController.open({ type: "info", message: caution });
    }

    await queueOnboardingStep(
      { dataSource, snackbarController },
      { path: entity.path, entityId: entity.id, queueTarget: "showcase" },
    );
  },
};

/**
 * `showcase_requests` — the ONE subcollection of an onboarding document, because showcases genuinely
 * recur per location (staff add showcase #2 next quarter) while brand / media / location / the
 * place-card CTA happen at most once and are embedded maps on the parent (design §3.2).
 *
 * ⚠️ ITS STATUS FIELDS SIT AT THE DOCUMENT ROOT — `submitState`, not `showcase.submitState`. One
 * request per document, so there is nothing to nest under. Every helper in this surface signals that
 * by taking a step name of `null`.
 *
 * ⚠️ AND IT IS THE ONE STEP THE DRAIN FINDS WITH A COLLECTION-GROUP QUERY on `submitState`, which is
 * the single hand-declared Firestore index in this chain. Nothing here depends on that, but a
 * document written outside this collection's path will not be drained.
 *
 * ⚠️ THE THREE IDS IN `input` ARE APPLE'S, AND THEY ARE THE HAZARD. `locationId`, `brandId` and
 * `mediaId` are staff-typed strings that flow into a real Apple write. A mistyped `locationId`
 * belonging to ANOTHER of this same client's locations puts a live showcase on the wrong place card,
 * and Apple's showcase endpoints take no client-supplied key, so undoing it is manual. The backend
 * pins all three against the parent's BACKEND-OWNED buckets; `evaluateQueueGate` repeats those pins
 * here so the answer arrives before the write.
 */
export const appleOnboardingShowcaseRequestsCollection = buildCollection<
  Partial<OnboardingShowcaseRequestEntity>
>({
  id: ONBOARDING_SHOWCASE_REQUESTS_PATH,
  path: ONBOARDING_SHOWCASE_REQUESTS_PATH,
  name: "Showcases",
  singularName: ONBOARDING_SHOWCASE_LABEL,
  description:
    "Showcase cards for this location's Apple place card. Fill in the details, then use the Queue tab — the ids must match what the brand, media and location steps recorded.",
  icon: "campaign",
  // The CMS chooses the document id; nothing about a showcase is a natural key (a location has many
  // over time), unlike the parent document's `${accountId}__${locationId}`.
  // Same convention as the parent collection — UI-only either way (the Firestore rules let any
  // authenticated principal write, design §3.1), but hardcoded `true` here while the parent gated on
  // roles was an inconsistency that read as an intentional exception.
  permissions: ({ authController }) => ({
    read: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    edit: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    create: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    // Deleting a request does NOT delete anything at Apple. Restricted to the same roles that may
    // queue, so the audit trail for a showcase that DID reach Apple is not casually removed.
    delete: hasAnyRole(authController, ONBOARDING_QUEUE_ROLES),
  }),
  entityActions: [queueShowcaseAction],
  entityViews: [
    {
      key: "queue_showcase",
      name: "Queue",
      position: "start",
      Builder: OnboardingShowcaseView,
    },
    {
      // 🛑 THE DESTRUCTIVE TAB. Extend / deactivate / delete, each behind a confirmation. It is a TAB
      // rather than a row action because `EntityAction.onClick` in this FireCMS beta cannot render a
      // dialog, and a destructive action must not be one click away. Deliberately NOT `position:
      // "start"`: the Queue tab stays the landing tab, so opening a showcase request does not put a
      // Delete button under the cursor.
      key: "showcase_lifecycle",
      name: "Lifecycle",
      Builder: ShowcaseLifecycleView,
    },
  ],
  // The read-only audit trail of every lifecycle action asked for on this showcase. A subcollection
  // rather than a root collection so it needs no navigation entry and cannot be reached — or written —
  // except through the showcase it belongs to.
  subcollections: [appleShowcaseLifecycleRequestsCollection],
  callbacks: {
    // The whole write boundary for the form, in one line. See `stripFieldsTheFormMayNotWrite`.
    onPreSave: ({ values }) => stripFieldsTheFormMayNotWrite(values),
  },
  // ⚠️ Must list EVERY property key: `getFormFieldKeys` filters the entity form through this same
  // array, so an omitted key disappears from the detail view too, not only from the table. Table
  // trimming is `hideFromCollection`'s job.
  propertiesOrder: [
    "status",
    "input",
    "companyId",
    "submitState",
    "queuedAt",
    "attempts",
    "failureCode",
    "failureMessage",
    "writeError",
    "claimedAt",
    "completedAt",
    "apple",
  ],
  properties: {
    input: buildProperty({
      name: "Showcase details",
      description:
        "What Apple will publish on the place card. The three Apple ids must match what the brand, media and location steps recorded for this location — the backend refuses anything else.",
      dataType: "map",
      properties: {
        accountId: buildProperty({
          name: "DearDoc Account ID",
          description:
            "Required by the shared schema. Use the same value as the parent record — the parent is the authority if they ever disagree.",
          dataType: "string",
        }),
        locationId: buildProperty({
          name: "Apple Location ID",
          description:
            "⚠️ APPLE's location id, not DearDoc's. Copy it from the location step's Apple result. A wrong id belonging to another of this client's locations would put a live showcase on the wrong place card, which Apple gives us no way to undo — so the backend refuses any value that does not match.",
          dataType: "string",
        }),
        brandId: buildProperty({
          name: "Apple Brand ID",
          description:
            "⚠️ APPLE's brand id. Copy it from the brand step's Apple result; the backend refuses a brand it did not create for this location.",
          dataType: "string",
        }),
        mediaId: buildProperty({
          name: "Apple Image ID",
          description:
            "⚠️ APPLE's image id. Apple refuses a showcase creative built on an image it has not APPROVED, and approval takes a few business days.",
          dataType: "string",
        }),
        headline: buildProperty({
          name: "Headline",
          description:
            "Required, 5 to 38 characters. The bold line on the showcase card — keep it short enough to read at a glance. Anything outside that range is refused before the showcase is sent, so trim it here.",
          dataType: "string",
        }),
        body: buildProperty({
          name: "Body",
          description:
            "Required, 5 to 100 characters. One sentence under the headline. Anything outside that range is refused before the showcase is sent.",
          dataType: "string",
          multiline: true,
        }),
        altText: buildProperty({
          name: "Alt text",
          description:
            "Accessibility description for the image — what a screen reader announces. Required, 5 to 255 characters. Describe the subject; do not start with “photo of”.",
          dataType: "string",
        }),
        callToAction: buildProperty({
          name: "Call to action",
          description:
            "The two values DearDoc actually uses — a shortlist, NOT the limit of what Apple accepts. Apple's showcase CTA vocabulary is much larger and its API types this as an open string. Defaults to SCHEDULE, the booking button. The real gate is at runtime: the backend asks Apple which CTAs this brand and location actually offer, and refuses to submit one Apple has not itself named with a provider behind it.",
          dataType: "string",
          defaultValue: ShowcaseCreativeCtaEnum.SCHEDULE,
          enumValues: SHOWCASE_CTA_LABELS,
        }),
        startDate: buildProperty({
          name: "Start date",
          description:
            "⚠️ A STRING, not a date picker: the backend parses these with a string schema (JSON has no date type) and a Firestore timestamp would fail that parse. Use UTC-midnight ISO-8601, e.g. 2026-09-01T00:00:00Z.",
          dataType: "string",
        }),
        endDate: buildProperty({
          name: "End date (EXCLUSIVE — read this)",
          description:
            "⚠️ EXCLUSIVE: the showcase is NOT visible on this date. It stops being visible the day BEFORE. So for visibility through Oct 5, enter Oct 6 — entering Oct 5 makes the showcase go dark on Oct 4, one day early, silently and with no error. Same format as the start date. Must be after it, and Apple caps the flight window at 30 days (counted as end minus start, with no +1).",
          dataType: "string",
        }),
        locale: buildProperty({
          name: "Locale",
          description: "Optional, e.g. en-US.",
          dataType: "string",
        }),
      },
    }),
    ...stepStatusProperties({ resourceNoun: "showcase" }),
  },
  additionalFields: [
    {
      key: "status",
      name: "Status",
      dependencies: ["submitState", "apple"],
      Builder: ShowcaseRequestChip,
    },
  ],
});
